import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Select from 'react-select';
import {
    Send as SendIcon,
    AutoAwesome as AIIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    MoreVert as MoreIcon,
    FilterList as FilterIcon,
    Inbox as InboxIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Star as StarFilledIcon,
    SendOutlined as SentIcon,
    ArchiveOutlined as ArchiveIcon,
    UnarchiveOutlined as UnarchiveIcon,
    DeleteOutline as TrashIcon,
    Circle as CircleIcon,
    EmailOutlined as EmailIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    AttachFile as AttachFileIcon,
    InsertDriveFile as FileIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { CircularProgress, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { uploadFileToCampusFileSystem, getFileById } from '../../services/CampusFileSystemService';
import { MiniLoader } from '../../components/Common/LoadingScreen';
import { INBOX_CATEGORIES, AI_TEMPLATES, USER_TYPE_COLORS } from './InboxConstants';
import { stripHtml } from '../../utils/textUtils';
import './ERPInbox.css';

const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '44px',
        borderRadius: '10px',
        borderColor: state.isFocused ? '#014486' : '#cbd5e1',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(1, 68, 134, 0.15)' : 'none',
        backgroundColor: '#ffffff',
        '&:hover': {
            borderColor: '#94a3b8'
        }
    }),
    valueContainer: (base) => ({
        ...base,
        maxHeight: '100px',
        overflowY: 'auto',
        padding: '2px 6px'
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '6px',
        margin: '2px'
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#1e40af',
        fontWeight: 600,
        fontSize: '12px',
        padding: '2px 6px'
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#1e40af',
        ':hover': {
            backgroundColor: '#dbeafe',
            color: '#1e3a8a'
        }
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999
    })
};

const ERPInbox = () => {
    const { user, token, axios: authAxios, userRole, orgId, refreshUnreadCount } = useAuth();
    const [selectedTab, setSelectedTab] = useState('inbox');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [isComposeOpen, setIsComposeOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // --- Backend Data State ---
    const [mailboxItems, setMailboxItems] = useState([]);
    const [selectedConversationMessages, setSelectedConversationMessages] = useState([]);
    const { unreadCount: globalUnreadCount } = useAuth();
    const [isLoadingMailbox, setIsLoadingMailbox] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [mailboxLastKey, setMailboxLastKey] = useState(null);
    const [messagesLastKey, setMessagesLastKey] = useState(null);
    const [replyBody, setReplyBody] = useState('');
    const [isSendingReply, setIsSendingReply] = useState(false);
    const [selectedComposeCategory, setSelectedComposeCategory] = useState(null);
    const [listWidth, setListWidth] = useState(380); // in pixels
    const [isListCollapsed, setIsListCollapsed] = useState(false);
    const isResizing = useRef(false);
    const threadEndRef = useRef(null);

    // ── Starred: backend-synced ──
    const toggleStar = async (conversationId, currentlyStarred) => {
        if (!user?.userId || !token) return;
        const newStatus = currentlyStarred ? 'UNFAVOURITE' : 'FAVOURITE';
        try {
            await authAxios.patch(configData.get_inbox_conversation_status(conversationId), null, {
                params: { userId: user?.userId, status: newStatus }
            });
            // Update local state for immediate feedback
            setMailboxItems(prev => {
                if (selectedTab === 'starred' && currentlyStarred) {
                    return prev.filter(m => m.conversationId !== conversationId);
                }
                return prev.map(m =>
                    m.conversationId === conversationId ? { ...m, isFavourite: !currentlyStarred } : m
                );
            });
            if (selectedMessage?.conversationId === conversationId) {
                if (selectedTab === 'starred' && currentlyStarred) {
                    setSelectedMessage(null);
                } else {
                    setSelectedMessage(prev => ({ ...prev, isFavourite: !currentlyStarred }));
                }
            }
            toast.success(currentlyStarred ? 'Removed from favorites' : 'Added to favorites');
        } catch (err) {
            console.error("Error toggling star", err);
            toast.error("Failed to update favorite status");
        }
    };

    const bulkStar = async (ids, shouldStar = true) => {
        if (!user?.userId || !token || ids.length === 0) return;
        try {
            await authAxios.patch(configData.INBOX_BULK_STATUS, {
                conversationIds: ids,
                status: shouldStar ? 'FAVOURITE' : 'UNFAVOURITE'
            }, {
                params: { userId: user?.userId }
            });
            // Update local state
            setMailboxItems(prev => {
                if (selectedTab === 'starred' && !shouldStar) {
                    return prev.filter(m => !ids.includes(m.conversationId));
                }
                return prev.map(m =>
                    ids.includes(m.conversationId) ? { ...m, isFavourite: shouldStar } : m
                );
            });
            if (selectedMessage && ids.includes(selectedMessage.conversationId)) {
                if (selectedTab === 'starred' && !shouldStar) {
                    setSelectedMessage(null);
                } else {
                    setSelectedMessage(prev => ({ ...prev, isFavourite: shouldStar }));
                }
            }
            clearSelection();
            toast.success(`${ids.length} messages updated`);
        } catch (err) {
            console.error("Error in bulk star", err);
            toast.error("Failed to perform bulk update");
        }
    };

    // ── Multi-select ──
    const [selectedIds, setSelectedIds] = useState(new Set());
    const toggleSelect = (e, conversationId) => {
        e.stopPropagation();
        setSelectedIds(prev => { const next = new Set(prev); next.has(conversationId) ? next.delete(conversationId) : next.add(conversationId); return next; });
    };
    const clearSelection = () => setSelectedIds(new Set());

    const handleDownloadInboxAttachment = async (att) => {
        const displayName = att.filename || 'Document';
        const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
        const uuidMatch = (att.url || '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
        const fileId = att.fileId || (uuidMatch ? uuidMatch[0] : null);

        try {
            toast.info(`Downloading ${displayName}...`);
            let downloadUrl = att.url;

            if (fileId) {
                const freshDoc = await getFileById(fileId, rawToken, true);
                downloadUrl = freshDoc?.downloadUrl || freshDoc?.documentUrl || freshDoc?.url || downloadUrl;
            }

            if (!downloadUrl) {
                toast.error("Download link is not available.");
                return;
            }

            const response = await fetch(downloadUrl);
            if (!response.ok) {
                if (fileId) {
                    const retryDoc = await getFileById(fileId, rawToken, true);
                    const retryUrl = retryDoc?.downloadUrl;
                    if (retryUrl) {
                        const retryRes = await fetch(retryUrl);
                        if (retryRes.ok) {
                            const blob = await retryRes.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = displayName;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                            toast.success(`Downloaded ${displayName}`);
                            return;
                        }
                    }
                }
                throw new Error("Download request expired or file inaccessible. Please try again.");
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = displayName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toast.success(`Downloaded ${displayName}`);
        } catch (err) {
            console.error("Failed to download attachment:", err);
            toast.error(err.message || "Could not download attachment.");
        }
    };

    const renderMessageBody = (rawBody) => {
        if (!rawBody) return '(Empty message)';

        let isHtml = /<[a-z][\s\S]*>/i.test(rawBody) || /&lt;[a-z]/i.test(rawBody);
        if (isHtml) {
            let htmlContent = String(rawBody);
            if (htmlContent.includes('&lt;') && htmlContent.includes('&gt;')) {
                try {
                    const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
                    htmlContent = doc.body.textContent || doc.body.innerText || htmlContent;
                } catch (e) {
                    htmlContent = htmlContent.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
                }
            }
            return (
                <div 
                    className="inbox-rendered-html-body"
                    style={{ lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            );
        }

        const attachments = [];
        const paymentLinks = [];
        const markdownLinkRegex = /\[([^\]]+)\]\(((?:https?|upi|intent):\/\/[^\s\)]+)\)/gi;

        let match;
        while ((match = markdownLinkRegex.exec(rawBody)) !== null) {
            const label = match[1];
            const url = match[2];
            if (label.toLowerCase().includes('qr') || label.toLowerCase().includes('document') || url.includes('qrserver') || url.includes('.pdf') || url.includes('campus-file-system')) {
                attachments.push({ filename: label, url });
            } else {
                paymentLinks.push({ label, url });
            }
        }

        let cleanText = rawBody
            .replace(/Attachments:\s*(?:📎\s*(?:Document:)?\s*\[[^\]]+\]\((?:https?|upi|intent):\/\/[^\s\)]+\)\s*)*/gi, '')
            .trim();

        // Parse inline links and text parts
        const parts = [];
        const fullRegex = /\[([^\]]+)\]\(((?:https?|upi|intent):\/\/[^\s\)]+)\)|((?:https?|upi|intent):\/\/[^\s\)]+)/gi;
        let lastIndex = 0;
        let m;

        while ((m = fullRegex.exec(cleanText)) !== null) {
            if (m.index > lastIndex) {
                parts.push(cleanText.substring(lastIndex, m.index));
            }
            if (m[1] && m[2]) {
                const linkText = m[1];
                const linkUrl = m[2];
                const isPayment = linkUrl.startsWith('upi:') || linkText.toLowerCase().includes('pay') || linkUrl.includes('pay');
                
                parts.push(
                    <a
                        key={m.index}
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: isPayment ? '#059669' : '#014486',
                            fontWeight: isPayment ? 700 : 600,
                            textDecoration: 'underline'
                        }}
                    >
                        {linkText}
                    </a>
                );
            } else if (m[3]) {
                const linkUrl = m[3];
                const isPayment = linkUrl.startsWith('upi:') || linkUrl.includes('pay');
                parts.push(
                    <a
                        key={m.index}
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: isPayment ? '#059669' : '#014486',
                            fontWeight: isPayment ? 700 : 500,
                            textDecoration: 'underline'
                        }}
                    >
                        {linkUrl}
                    </a>
                );
            }
            lastIndex = fullRegex.lastIndex;
        }
        if (lastIndex < cleanText.length) {
            parts.push(cleanText.substring(lastIndex));
        }

        return (
            <div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {parts.length > 0 ? parts : cleanText}
                </div>

                {/* Quick Pay Buttons */}
                {paymentLinks.length > 0 && (
                    <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {paymentLinks.map((pl, idx) => (
                            <a
                                key={idx}
                                href={pl.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: 'linear-gradient(135deg, #059669, #10b981)',
                                    color: '#ffffff',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    textDecoration: 'none',
                                    boxShadow: '0 4px 10px rgba(5, 150, 105, 0.25)',
                                    transition: 'transform 0.15s'
                                }}
                            >
                                💳 {pl.label || 'Click to Pay via UPI'}
                            </a>
                        ))}
                    </div>
                )}

                {/* Document Attachments & QR Codes */}
                {attachments.length > 0 && (
                    <div className="message-attachments-container" style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            📎 Attached Documents & Payment QR ({attachments.length})
                        </div>
                        {attachments.map((att, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                <FileIcon style={{ color: '#014486', fontSize: '24px' }} />
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        {att.filename}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDownloadInboxAttachment(att)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        background: '#014486',
                                        color: '#ffffff',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        border: 'none',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <DownloadIcon style={{ fontSize: '16px' }} /> Download / View
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const categories = INBOX_CATEGORIES;

    // --- Advanced Targeting State ---
    const [targetingTab, setTargetingTab] = useState((userRole === 'ADMIN' || userRole === 'FACULTY') ? 'global' : 'class');
    const [globalTarget, setGlobalTarget] = useState('ALL'); // 'ALL', 'STUDENTS', 'FACULTY', 'STAFF'
    const [classOptions, setClassOptions] = useState([]);
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedIndividualUsers, setSelectedIndividualUsers] = useState([]);
    const [selectedClassForFilter, setSelectedClassForFilter] = useState(null);
    const [isLoadingStudents, setIsLoadingStudents] = useState(false);
    const [individualRoleFilter, setIndividualRoleFilter] = useState('STUDENT'); // Admin role filter in individual tab

    const handleResizeStart = (e) => {
        e.preventDefault();
        isResizing.current = true;
        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = 'col-resize';
    };

    const handleResizeMove = (e) => {
        if (!isResizing.current) return;
        const sidebarWidth = 240; 
        const newWidth = e.clientX - sidebarWidth;
        if (newWidth > 250 && newWidth < 800) {
            setListWidth(newWidth);
        }
    };

    const handleResizeEnd = () => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = 'default';
    };

    const fetchUnreadCount = React.useCallback(async () => {
        if (!user?.userId || !token) return;
        try {
            const res = await authAxios.get(configData.INBOX_UNREAD_COUNT, {
                params: { userId: user?.userId, orgId: user?.orgId }
            });
            // Sync global topbar count
            refreshUnreadCount();
        } catch (err) { console.error("Error fetching unread count", err); }
    }, [user?.userId, token, authAxios, refreshUnreadCount]);

    const fetchMailbox = React.useCallback(async (lastKey = null, isLoadMore = false) => {
        if (!user?.userId || !token) return;
        setIsLoadingMailbox(true);
        try {
            const folderMap = { inbox: 'INBOX', sent: 'SENT', archive: 'ARCHIVED', trash: 'DELETED', starred: 'FAVOURITES' };
            const folder = folderMap[selectedTab] || 'INBOX';

            // Map frontend category name to backend enum
            const categoryMap = { 'Announcements': 'ANNOUNCEMENT', 'Assignments': 'ASSIGNMENT', 'Queries': 'QUERY', 'Alerts': 'ALERT' };
            const category = selectedCategory ? categoryMap[selectedCategory] : null;

            console.log(`Fetching mailbox: folder=${folder}, category=${category}`);

            const res = await authAxios.get(configData.INBOX_MAILBOX, {
                params: { 
                    userId: user?.userId, 
                    folder, 
                    category,
                    lastKey, 
                    size: 20, 
                    orgId: user?.orgId 
                }
            });

            const newItems = res.data.items || [];
            const combinedItems = isLoadMore ? [...mailboxItems, ...newItems] : newItems;

            // Group by conversationId
            const groupedMap = new Map();
            combinedItems.forEach(item => {
                const existing = groupedMap.get(item.conversationId);
                if (!existing || new Date(item.lastMessageAt) > new Date(existing.lastMessageAt)) {
                    groupedMap.set(item.conversationId, {
                        ...item,
                        unreadCount: (existing?.unreadCount || 0) + (item.unreadCount || 0),
                        allMailboxIds: [...(existing?.allMailboxIds || []), item.userMailboxId]
                    });
                } else {
                    existing.unreadCount += (item.unreadCount || 0);
                    if (!existing.allMailboxIds.includes(item.userMailboxId)) {
                        existing.allMailboxIds.push(item.userMailboxId);
                    }
                }
            });

            const finalItems = Array.from(groupedMap.values()).sort((a, b) => 
                new Date(b.lastMessageAt) - new Date(a.lastMessageAt)
            );

            setMailboxItems(finalItems);
            setMailboxLastKey(res.data.nextKey);
        } catch (err) {
            console.error("Error fetching mailbox", err);
        } finally {
            setIsLoadingMailbox(false);
        }
    }, [user?.userId, token, selectedTab, selectedCategory, authAxios]);

    const fetchConversationMessages = async (conversationId, lastKey = null, isLoadMore = false) => {
        if (!user?.userId || !token || !conversationId) return;
        setIsLoadingMessages(true);
        try {
            const res = await authAxios.get(`${configData.INBOX_CONVERSATION}/${conversationId}/messages`, {
                params: { userId: user.userId, lastKey, size: 50, orgId: user?.orgId }
            });

            if (isLoadMore) {
                setSelectedConversationMessages(prev => [...prev, ...(res.data.items || [])]);
            } else {
                setSelectedConversationMessages(res.data.items || []);
            }
            setMessagesLastKey(res.data.nextKey);
        } catch (err) {
            console.error("Error fetching conversation messages", err);
        } finally {
            setIsLoadingMessages(false);
            // After fetching messages, the backend now auto-marks as read.
            // We should refresh the unread count to update the global badge instantly.
            fetchUnreadCount();
        }
    };

    const updateStatus = async (conversationId, status, mailboxIds = []) => {
        if (!user?.userId || !token || !conversationId) return;
        try {
            await authAxios.patch(`${configData.INBOX_CONVERSATION}/${conversationId}/status`, null, {
                params: { userId: user.userId, status, orgId: user?.orgId }
            });
            // Update local state
            if (status === 'ARCHIVED' || status === 'DELETED') {
                setMailboxItems(prev => prev.filter(item => item.conversationId !== conversationId));
                if (selectedMessage?.conversationId === conversationId) setSelectedMessage(null);
            } else if (status === 'READ') {
                console.log(`Marking conversation ${conversationId} as read locally`);
                setMailboxItems(prev => prev.map(item => 
                    item.conversationId === conversationId ? { ...item, unreadCount: 0 } : item
                ));
                fetchUnreadCount();
            }
        } catch (err) { console.error("Error updating status", err); }
    }

    useEffect(() => {
        fetchMailbox();
        fetchUnreadCount();

        // Background polling for new messages - increased frequency for active use
        const interval = setInterval(() => {
            fetchMailbox();
            fetchUnreadCount();
        }, 15000); // 15 seconds

        return () => clearInterval(interval);
    }, [fetchMailbox, fetchUnreadCount]);

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [selectedConversationMessages]);

    // Auto-read selected message if new replies arrive while viewing
    useEffect(() => {
        if (selectedMessage) {
            const currentItem = mailboxItems.find(item => item.conversationId === selectedMessage.conversationId);
            if (currentItem && currentItem.unreadCount > 0) {
                console.log(`[Inbox] Auto-marking active thread ${selectedMessage.conversationId} as read. Current unread in list: ${currentItem.unreadCount}`);
                
                // 1. Mark as read on backend (pass all mailboxIds to handle duplicates)
                updateStatus(selectedMessage.conversationId, 'READ', currentItem.allMailboxIds);
                
                // 2. Refresh messages to show new ones
                fetchConversationMessages(selectedMessage.conversationId);
                
                // 3. Update our selectedMessage reference locally so we don't double-trigger
                setSelectedMessage(prev => ({ ...prev, unreadCount: 0 }));

                // 4. Force update the item in our current list to avoid flickering during next poll
                setMailboxItems(prev => prev.map(m => 
                    m.conversationId === selectedMessage.conversationId ? { ...m, unreadCount: 0 } : m
                ));
            }
        }
    }, [mailboxItems, selectedMessage?.conversationId]);

    const handleSelectMessage = (item) => {
        setSelectedMessage(item);
        // fetchConversationMessages now triggers a backend auto-read (fix: 8afb30d)
        fetchConversationMessages(item.conversationId);

        // Instant UI feedback: mark as read locally
        setMailboxItems(prev => prev.map(msg =>
            msg.conversationId === item.conversationId ? { ...msg, unreadCount: 0 } : msg
        ));
    };

    // --- Filtering Logic (Mostly handled by backend folders) ---
    const filteredMessages = mailboxItems.filter(m => {
        // Search filtering (client-side)
        if (searchQuery && !m.subject.toLowerCase().includes(searchQuery.toLowerCase()) && !m.participantSummary.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
    });



    // --- Compose UI State ---
    const [subject, setSubject] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });
    const [isSending, setIsSending] = useState(false);
    const [showAssistant, setShowAssistant] = useState(true);
    const [officialTemplates, setOfficialTemplates] = useState([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

    // --- Attachment UI State (Campus File System Integration) ---
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const attachmentInputRef = useRef(null);

    const handleAttachmentSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingAttachment(true);
        const tempId = `temp-${Date.now()}`;
        const tempRecord = { id: tempId, filename: file.name, size: file.size, status: 'UPLOADING' };
        setAttachedFiles(prev => [...prev, tempRecord]);

        try {
            const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
            const record = await uploadFileToCampusFileSystem(file, {
                entityType: 'USER',
                entityId: user?.userId || orgId || 'user',
                orgId: orgId || 'DEFAULT',
                category: 'OTHER',
                uploadedBy: user?.userId || 'user',
                token: rawToken
            });

            toast.success(`Attached document: ${file.name}`);
            setAttachedFiles(prev => prev.map(f => f.id === tempId ? {
                id: record.fileId || tempId,
                fileId: record.fileId,
                filename: file.name,
                downloadUrl: record.downloadUrl,
                size: file.size,
                status: 'ACTIVE'
            } : f));
        } catch (err) {
            console.error("Attachment upload failed:", err);
            toast.error(err.message || "Failed to attach document.");
            setAttachedFiles(prev => prev.filter(f => f.id !== tempId));
        } finally {
            setIsUploadingAttachment(false);
            if (e.target) e.target.value = '';
        }
    };

    const removeAttachment = (fileId) => {
        setAttachedFiles(prev => prev.filter(f => (f.fileId || f.id) !== fileId));
    };

    const useCases = [
        { id: 'fee_reminder', title: 'Fee Reminder', intent: 'fee_reminder' },
        { id: 'low_attendance', title: 'Attendance Alert', intent: 'attendance_alert' },
        { id: 'appreciation', title: 'Staff Appreciation', intent: 'appreciation' },
        { id: 'event_announcement', title: 'Event Update', intent: 'event_announcement' }
    ];

    const fetchTargetingData = React.useCallback(async () => {
        if (!orgId || !token) return;
        try {
            const classRes = await authAxios.get(configData.getClassesByOrgUrl(user.orgId));
            setClassOptions(classRes.data.map(c => ({ value: c.classId, label: `${c.className} - ${c.sectionName}` })));

            // Admin can message Faculty, Staff, Admin, and Students
            // Faculty can message Students only
            if (userRole === 'ADMIN' || userRole === 'FACULTY') {
                const [facultyRes, staffRes, adminRes] = await Promise.all([
                    authAxios.get(`${configData.GET_USERS_BY_TYPE}/${user.orgId}`, { params: { userType: 'FACULTY', page: 1, pageSize: 500 } }),
                    authAxios.get(`${configData.GET_USERS_BY_TYPE}/${user.orgId}`, { params: { userType: 'STAFF', page: 1, pageSize: 500 } }),
                    authAxios.get(`${configData.GET_USERS_BY_TYPE}/${user.orgId}`, { params: { userType: 'ADMIN', page: 1, pageSize: 500 } }),
                ]);
                const toOption = (u, roleLabel, roleType) => ({
                    value: u.user?.userId || u.userId,
                    label: `${u.user?.firstName || u.firstName} ${u.user?.lastName || u.lastName} (${roleLabel})`,
                    type: roleType
                });
                const getItems = (res) => res.data?.items || res.data?.content || [];
                const combinedUsers = [
                    ...getItems(facultyRes).map(u => toOption(u, 'Faculty', 'FACULTY')),
                    ...getItems(staffRes).map(u => toOption(u, 'Staff', 'STAFF')),
                    ...getItems(adminRes).map(u => toOption(u, 'Admin', 'ADMIN')),
                ];
                console.log('[ERPInbox] Loaded individual targets:', combinedUsers.length, 'users');
                setUserOptions(combinedUsers);
            } else {
                // Faculty/Staff: individual tab shows only students (loaded via class filter)
                setUserOptions([]);
            }
        } catch (err) { console.error("Error fetching targeting data", err); }
    }, [token, orgId, authAxios, userRole]);

    const fetchStudentsByClass = async (classId) => {
        if (!classId || !token) return;
        setIsLoadingStudents(true);
        try {
            const res = await authAxios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`);
            const students = (res.data.items || res.data || []).map(s => ({
                value: s.user?.userId || s.userId, // Use user.userId (UUID)
                label: `${s.user?.firstName || s.firstName} ${s.user?.lastName || s.lastName} (Student)`,
                type: 'STUDENT'
            }));
            // Add students to userOptions if not already there, or just show them
            setUserOptions(prev => {
                const nonStudents = prev.filter(u => u.type !== 'STUDENT');
                return [...nonStudents, ...students];
            });
        } catch (err) {
            console.error("Error fetching students", err);
        } finally {
            setIsLoadingStudents(false);
        }
    };

    useEffect(() => {
        if (selectedClassForFilter && selectedClassForFilter.value) {
            fetchStudentsByClass(selectedClassForFilter.value);
        } else {
            setUserOptions(prev => prev.filter(u => u.type !== 'STUDENT'));
        }
    }, [selectedClassForFilter]);

    const handleClassFilterChange = (selectedOpt) => {
        setSelectedClassForFilter(selectedOpt);
        if (!selectedOpt || !selectedOpt.value) {
            setUserOptions(prev => prev.filter(u => u.type !== 'STUDENT'));
            setSelectedIndividualUsers(prev => prev.filter(u => u.type !== 'STUDENT'));
        } else {
            setSelectedIndividualUsers(prev => prev.filter(u => u.type !== 'STUDENT'));
        }
    };

    const fetchTemplates = useCallback(async () => {
        if (!token || !orgId) return;
        setIsLoadingTemplates(true);
        try {
            const res = await authAxios.get(configData.GET_NOTIFICATION_TEMPLATES, {
                params: { orgId: user?.orgId }
            });
            // Handle both flat array and { items: [] } structure
            const data = Array.isArray(res.data) ? res.data : (res.data.items || []);
            setOfficialTemplates(data);
        } catch (err) {
            console.error("Error fetching templates", err);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, [token, user?.orgId, authAxios]);

    useEffect(() => {
        if (isComposeOpen) {
            fetchTargetingData();
            fetchTemplates();
        }
    }, [isComposeOpen, fetchTargetingData, fetchTemplates]);

    const handleAskAI = async (intent = '', customPrompt = '') => {
        setIsGenerating(true);
        try {
            const context = `Targeting: ${targetingTab === 'global' ? globalTarget : targetingTab === 'class' ? 'Selected Classes' : 'Specific Individuals'}. ${customPrompt || aiPrompt}`;
            const res = await authAxios.post(configData.AI_NOTIFICATION_GENERATE, {
                userId: user?.userId,
                intent: intent || 'general',
                targetAudience: targetingTab === 'global' ? globalTarget : 'SPECIFIC_GROUP',
                additionalContext: context
            });
            const draft = typeof res.data === 'string' ? res.data : res.data.draft || res.data.content || res.data.text || JSON.stringify(res.data);
            setMessageBody(draft);
        } catch (err) {
            console.error(err);
        } finally { setIsGenerating(false); }
    };

    const handleSend = async () => {
        const trimmedBody = messageBody?.trim();
        if (!trimmedBody) {
            setStatusMsg({ type: 'error', text: 'Message body cannot be empty.' });
            return;
        }
        setIsSending(true);

        let finalBody = trimmedBody;
        if (attachedFiles.length > 0) {
            const attachmentLinks = attachedFiles
                .filter(a => a.status === 'ACTIVE' && a.downloadUrl)
                .map(a => `📎 Document: [${a.filename}](${a.downloadUrl})`)
                .join('\n');
            if (attachmentLinks) {
                finalBody += `\n\nAttachments:\n${attachmentLinks}`;
            }
        }

        const attachmentUrls = attachedFiles
            .filter(a => a.status === 'ACTIVE' && a.downloadUrl)
            .map(a => a.downloadUrl);

        const common = {
            subject: subject?.trim() || 'No Subject',
            body: finalBody,
            conversationId: null,
            orgId: orgId,
            attachmentUrls: attachmentUrls.length > 0 ? attachmentUrls : undefined
        };

        let targets = [];
        if (targetingTab === 'individual') {
            targets = selectedIndividualUsers.map(u => ({ type: u.type || 'USER', id: u.value }));
        } else if (targetingTab === 'class') {
            targets = selectedClasses.map(c => ({ type: 'CLASS', id: c.value }));
        } else {
            if (globalTarget === 'ALL') {
                targets = [
                    { type: 'ALL_STUDENTS', id: null },
                    { type: 'ALL_FACULTY', id: null },
                    { type: 'ALL_STAFF', id: null }
                ];
            } else {
                const targetTypeMap = { 'STUDENTS': 'ALL_STUDENTS', 'FACULTY': 'ALL_FACULTY', 'STAFF': 'ALL_STAFF' };
                targets = [{ type: targetTypeMap[globalTarget] || 'ALL_STUDENTS', id: null }];
            }
        }

        if (targets.length === 0) {
            setStatusMsg({ type: 'error', text: 'No recipients selected.' });
            setIsSending(false);
            return;
        }

        let successCount = 0;
        let failCount = 0;
        let firstFailReason = '';

        try {
            for (let i = 0; i < targets.length; i++) {
                const t = targets[i];
                setStatusMsg({ type: 'info', text: `Sending ${i + 1} of ${targets.length}...` });

                // Map frontend types to backend MessageTargetType enum
                let targetType = 'INDIVIDUAL';
                if (t.type === 'CLASS') targetType = 'CLASS';
                else if (t.type === 'ALL_STUDENTS') targetType = 'ALL_STUDENTS';
                else if (t.type === 'ALL_FACULTY') targetType = 'ALL_FACULTY';
                else if (t.type === 'ALL_STAFF') targetType = 'ALL_STAFF';

                // Map compose category to backend enum
                const categoryMap = { 'Announcements': 'ANNOUNCEMENT', 'Assignments': 'ASSIGNMENT', 'Queries': 'QUERY', 'Alerts': 'ALERT' };
                const composeCategory = selectedComposeCategory ? categoryMap[selectedComposeCategory] : null;

                const payload = {
                    ...common,
                    targetType: targetType,
                    targetId: t.id ? String(t.id) : null,
                    category: composeCategory
                };

                console.log(`Sending to ${targetType} (ID: ${t.id})`, payload);
                try {
                    await authAxios.post(`${configData.INBOX_SEND}`, payload, {
                        params: { userId: user.userId }
                    });
                    successCount++;
                } catch (singleErr) {
                    console.error(`Error sending to ${t.id}:`, singleErr);
                    if (!firstFailReason) firstFailReason = singleErr.response?.data?.message || singleErr.message || "Unknown error";
                    failCount++;
                }
            }

            if (failCount === 0) {
                setStatusMsg({ type: 'success', text: `Successfully sent ${successCount} message(s)!` });
                setTimeout(() => {
                    setIsComposeOpen(false);
                    setStatusMsg({ type: '', text: '' });
                    setMessageBody('');
                    setSubject('');
                    setAttachedFiles([]);
                    setSelectedComposeCategory(null);
                    setSelectedTab('sent');
                    fetchMailbox();
                }, 1500);
            } else {
                setStatusMsg({
                    type: 'error',
                    text: `Sent ${successCount}, but ${failCount} failed. Error: ${firstFailReason}`
                });
            }
        } catch (err) {
            console.error("General Send Error:", err);
            const serverMsg = err.response?.data?.message || err.message || "Unknown error";
            setStatusMsg({ type: 'error', text: `Error: ${serverMsg}` });
        } finally { setIsSending(false); }
    };

    const handleSendReply = async () => {
        if (!replyBody || !selectedMessage) return;
        setIsSendingReply(true);
        try {
            await authAxios.post(`${configData.INBOX_SEND}`, {
                subject: selectedMessage.subject,
                body: replyBody,
                conversationId: selectedMessage.conversationId,
                orgId,
                targetType: selectedMessage.targetType,
                targetId: null // Backend uses conversationId to determine recipients for replies
            }, {
                params: { userId: user?.userId }
            });
            setReplyBody('');
            toast.success("Reply sent!");
            fetchConversationMessages(selectedMessage.conversationId);
            fetchMailbox(); // Refresh preview in list
        } catch (err) {
            console.error("Error sending reply", err);
            const serverMsg = err.response?.data?.message || err.message || "Unknown error";
            toast.error(`Failed to send reply: ${serverMsg}`);
        } finally {
            setIsSendingReply(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    return (
        <div className="erp-inbox-outer">
            {/* Top Header */}
            <div className="erp-inbox-header">
                <div className="header-left">
                    <span className="header-title">
                        {selectedTab === 'inbox' ? 'Inbox' : 
                         selectedTab === 'sent' ? 'Sent Messages' : 
                         selectedTab === 'archive' ? 'Archived' : 
                         selectedTab === 'trash' ? 'Deleted' : 
                         selectedTab === 'starred' ? 'Favorites' : 'Messages'}
                    </span>
                    <div className="user-info">
                        <EmailIcon fontSize="small" />
                        <span>{`${user?.firstName || 'User'} ${user?.lastName || ''}`} - {user?.userType || 'Role'}</span>
                    </div>
                </div>
            </div>

            <div className={`erp-inbox-layout ${selectedMessage ? 'has-selection' : ''}`}>
                {/* Left Sidebar */}
                <aside className="inbox-sidebar">
                    <div className="compose-btn-wrapper">
                        <button className="compose-btn" onClick={() => setIsComposeOpen(true)}>
                            <EditIcon fontSize="small" />
                            Compose
                        </button>
                    </div>

                        <nav className="sidebar-nav">
                        <div className={`nav-item ${selectedTab === 'inbox' ? 'active' : ''}`} onClick={() => { setSelectedTab('inbox'); setSelectedCategory(null); clearSelection(); }}>
                            <div className="nav-label"><InboxIcon fontSize="small" /> Inbox</div>
                            {globalUnreadCount > 0 && <span className="badge">{globalUnreadCount}</span>}
                        </div>
                        <div className={`nav-item ${selectedTab === 'starred' ? 'active' : ''}`} onClick={() => { setSelectedTab('starred'); setSelectedCategory(null); clearSelection(); }}>
                            <div className="nav-label"><StarIcon fontSize="small" style={{ color: selectedTab === 'starred' ? '#f59e0b' : undefined }} /> Favorites</div>
                        </div>
                        <div className={`nav-item ${selectedTab === 'sent' ? 'active' : ''}`} onClick={() => { setSelectedTab('sent'); setSelectedCategory(null); clearSelection(); }}>
                            <div className="nav-label"><SentIcon fontSize="small" /> {(userRole === 'ADMIN' || userRole === 'FACULTY') ? 'All Sent History' : 'Sent Messages'}</div>
                        </div>
                        <div className={`nav-item ${selectedTab === 'archive' ? 'active' : ''}`} onClick={() => { setSelectedTab('archive'); setSelectedCategory(null); clearSelection(); }}>
                            <div className="nav-label"><ArchiveIcon fontSize="small" /> Archive</div>
                        </div>
                    </nav>

                    <div className="sidebar-section-label">Categories</div>
                    <div className="category-group">
                        {categories.map(cat => (
                            <div
                                key={cat.name}
                                className={`category-item ${selectedCategory === cat.name ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(cat.name === selectedCategory ? null : cat.name)}
                            >
                                <CircleIcon className="cat-dot" style={{ color: cat.color, fontSize: '10px' }} />
                                {cat.name}
                            </div>
                        ))}
                    </div>
                </aside>

                {/* Middle Pane: Item List */}
                <section 
                    className={`message-list-pane ${isListCollapsed ? 'collapsed' : ''}`}
                    style={{
                        display: isListCollapsed ? 'none' : 'flex',
                        flex: isListCollapsed ? '0 0 0' : `0 0 ${selectedMessage ? listWidth + 'px' : '100%'}`,
                        width: isListCollapsed ? 0 : (selectedMessage ? listWidth + 'px' : 'auto'),
                        transition: isResizing.current ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    <div className="list-header">
                        <div className="search-box">
                            <SearchIcon fontSize="small" color="disabled" />
                            <input type="text" placeholder="Search messages..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                        </div>
                        {selectedMessage && (
                            <IconButton size="small" onClick={() => setIsListCollapsed(true)} title="Collapse List">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </div>
                    <div className="list-tools">
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                            <input type="checkbox" onChange={(e) => {
                                if (e.target.checked) setSelectedIds(new Set(filteredMessages.map(m => m.conversationId)));
                                else clearSelection();
                            }} checked={selectedIds.size > 0 && selectedIds.size === filteredMessages.length} />
                            <RefreshIcon fontSize="small" style={{ cursor: 'pointer' }} onClick={() => fetchMailbox()} />
                            <MoreIcon fontSize="small" style={{ cursor: 'pointer' }} />
                        </div>
                        <FilterIcon fontSize="small" style={{ cursor: 'pointer', color: '#64748b' }} />
                    </div>

                    {/* Bulk action toolbar */}
                    {selectedIds.size > 0 && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '8px 16px', background: '#eff6ff',
                            borderBottom: '1px solid #bfdbfe', fontSize: '13px'
                        }}>
                            <span style={{ color: '#1e40af', fontWeight: 600 }}>{selectedIds.size} selected</span>
                            <button
                                onClick={() => bulkStar([...selectedIds], true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #fbbf24', background: '#fffbeb', color: '#92400e', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                            >
                                <StarIcon fontSize="small" style={{ fontSize: '14px', color: '#f59e0b' }} /> Favorite
                            </button>
                            <button
                                onClick={() => bulkStar([...selectedIds], false)}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                            >
                                <StarBorderIcon fontSize="small" style={{ fontSize: '14px' }} /> Unfavorite
                            </button>
                            <button
                                onClick={clearSelection}
                                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '12px' }}
                            >
                                Cancel
                            </button>
                        </div>
                    )}

                    <div className="scrollable-list">
                        {isLoadingMailbox && mailboxItems.length === 0 ? (
                            <div className="list-loading"><CircularProgress size={24} /></div>
                        ) : filteredMessages.length === 0 ? (
                            <div className="list-empty">No messages found.</div>
                        ) : (
                            <>
                                {filteredMessages.map(msg => (
                                    <div
                                        key={msg.userMailboxId}
                                        className={`message-card ${selectedMessage?.conversationId === msg.conversationId ? 'selected' : ''} ${msg.unreadCount > 0 ? 'unread' : ''} ${selectedIds.has(msg.conversationId) ? 'checked' : ''}`}
                                        onClick={() => handleSelectMessage(msg)}
                                    >
                                        {/* Row checkbox */}
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(msg.conversationId)}
                                            onChange={(e) => toggleSelect(e, msg.conversationId)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', accentColor: '#2563eb', cursor: 'pointer', width: '14px', height: '14px' }}
                                        />
                                        <div style={{ position: 'relative', paddingLeft: '24px' }}>
                                        <div className="card-top">
                                            <div className="sender-info">
                                                <div className="avatar" style={{ background: USER_TYPE_COLORS[msg.targetType] || '#64748b' }}>{getInitials(msg.participantSummary)}</div>
                                                <div className="sender-details">
                                                    <div className="name">
                                                        {msg.participantSummary}
                                                        {msg.unreadCount > 0 && <CircleIcon style={{ color: '#2563eb', fontSize: '10px', marginLeft: '6px' }} />}
                                                    </div>
                                                    <div className="role">{msg.targetType}</div>
                                                </div>
                                            </div>
                                            <div className="card-meta">
                                                <span className="time">{new Date(msg.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                {/* Inline star toggle (visible by default on touch or when no hover actions) */}
                                                <span
                                                    className="inline-star"
                                                    onClick={(e) => { e.stopPropagation(); toggleStar(msg.conversationId, msg.isFavourite); }}
                                                    title={msg.isFavourite ? 'Remove from favorites' : 'Add to favorites'}
                                                >
                                                    {msg.isFavourite
                                                        ? <StarFilledIcon style={{ fontSize: '16px', color: '#f59e0b' }} />
                                                        : <StarBorderIcon style={{ fontSize: '16px', color: '#cbd5e1' }} />}
                                                </span>
                                            </div>
                                            <div className="card-hover-actions">
                                                <IconButton
                                                    size="small"
                                                    className="action-btn"
                                                    onClick={(e) => { e.stopPropagation(); updateStatus(msg.conversationId, selectedTab === 'archive' ? 'UNARCHIVE' : 'ARCHIVED'); }}
                                                    title={selectedTab === 'archive' ? 'Unarchive' : 'Archive'}
                                                >
                                                    {selectedTab === 'archive' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    className="action-btn"
                                                    onClick={(e) => { e.stopPropagation(); updateStatus(msg.conversationId, 'DELETED'); }}
                                                    title="Delete"
                                                >
                                                    <TrashIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    className="action-btn"
                                                    onClick={(e) => { e.stopPropagation(); toggleStar(msg.conversationId, msg.isFavourite); }}
                                                    title={msg.isFavourite ? 'Remove from favorites' : 'Add to favorites'}
                                                >
                                                    {msg.isFavourite
                                                        ? <StarFilledIcon fontSize="small" style={{ color: '#f59e0b' }} />
                                                        : <StarBorderIcon fontSize="small" />}
                                                </IconButton>
                                            </div>
                                        </div>
                                        <div className="card-content">
                                            <div className="card-subject">{msg.subject}</div>
                                            <div className="card-snippet">
                                                {stripHtml(msg.lastMessagePreview || 'No preview available')}
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                ))}
                                {mailboxLastKey && (
                                    <button className="load-more-btn" onClick={() => fetchMailbox(mailboxLastKey, true)} disabled={isLoadingMailbox}>
                                        {isLoadingMailbox ? <MiniLoader message="Syncing Mailbox" mini={true} /> : 'Load More'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </section>

                {/* Right Pane: Message Viewer — resizable inline panel */}
                {/* Inline Message Viewer */}
                <main
                    className="message-viewer-pane"
                    style={{
                        flex: !selectedMessage ? '0 0 0' : '1 1 0',
                        display: !selectedMessage ? 'none' : 'flex',
                        minWidth: isListCollapsed ? '100%' : '300px',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        transition: isResizing.current ? 'none' : 'flex 0.3s ease, width 0.3s ease',
                        position: 'relative',
                        flexDirection: 'column',
                        borderLeft: isListCollapsed ? 'none' : '1px solid #e2e8f0',
                        backgroundColor: '#fff'
                    }}
                >
                    {/* Resizer handle (left edge) */}
                    {selectedMessage && !isListCollapsed && (
                        <div
                            onMouseDown={handleResizeStart}
                            style={{
                                position: 'absolute',
                                left: -3,
                                top: 0,
                                bottom: 0,
                                width: '6px',
                                cursor: 'col-resize',
                                zIndex: 10,
                                transition: 'background 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            className="viewer-resizer-handle"
                        >
                            <div style={{ width: '2px', height: '24px', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
                        </div>
                    )}
                    {selectedMessage ? (
                        <div className="viewer-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            {/* Viewer header with window controls */}
                            <div 
                                className="viewer-header" 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px', 
                                    padding: '10px 16px', 
                                    borderBottom: '1px solid #e2e8f0', 
                                    background: '#f8fafc', 
                                    flexShrink: 0 
                                }}
                            >
                                {/* Controls */}
                                {isListCollapsed && (
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setIsListCollapsed(false)} 
                                            title="Show List"
                                            style={{ marginRight: '10px', padding: '4px' }}
                                        >
                                            <InboxIcon fontSize="small" style={{ fontSize: '16px' }} />
                                        </IconButton>
                                    </div>
                                )}
                                <h1 className="viewer-main-subject" style={{ flex: 1, margin: 0, fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {selectedMessage.subject}
                                </h1>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                    <IconButton size="small" onClick={() => updateStatus(selectedMessage.conversationId, selectedTab === 'archive' ? 'UNARCHIVE' : 'ARCHIVED')} title={selectedTab === 'archive' ? 'Unarchive' : 'Archive'}>
                                        {selectedTab === 'archive' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                                    </IconButton>
                                    <span
                                        onClick={() => toggleStar(selectedMessage.conversationId, selectedMessage.isFavourite)}
                                        title={selectedMessage.isFavourite ? 'Remove from favorites' : 'Add to favorites'}
                                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: selectedMessage.isFavourite ? '#f59e0b' : '#cbd5e1', marginRight: '8px' }}
                                    >
                                        {selectedMessage.isFavourite
                                            ? <StarFilledIcon style={{ fontSize: '18px' }} />
                                            : <StarBorderIcon style={{ fontSize: '18px' }} />}
                                    </span>
                                    <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 4px' }} />
                                    <IconButton
                                        size="small"
                                        onClick={() => { setSelectedMessage(null); setIsListCollapsed(false); }}
                                        title="Close Message"
                                        style={{ padding: '4px', color: '#64748b' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="viewer-body-thread" style={{ flex: 1, overflowY: 'auto' }}>
                                        {isLoadingMessages && selectedConversationMessages.length === 0 ? (
                                            <div className="thread-loading"><CircularProgress /></div>
                                        ) : (
                                            selectedConversationMessages.map((m) => (
                                                <div key={m.messageId} className={`thread-message ${m.senderId === user?.userId ? 'sent' : 'received'}`}>
                                                    <div className="message-metadata">
                                                        <span className="sender-name">{m.senderName}</span>
                                                        <span className="message-time">{new Date(m.createdAt).toLocaleString()}</span>
                                                    </div>
                                                    <div className="message-content-bubble">
                                                        {renderMessageBody(m.body)}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                        {messagesLastKey && (
                                            <button className="load-more-thread-btn" onClick={() => fetchConversationMessages(selectedMessage.conversationId, messagesLastKey, true)} disabled={isLoadingMessages}>
                                                Load older messages
                                            </button>
                                        )}
                                        <div ref={threadEndRef} />
                                    </div>
                                    <div className="viewer-reply-area">
                                        <textarea
                                            placeholder="Type your reply..."
                                            value={replyBody}
                                            onChange={(e) => setReplyBody(e.target.value)}
                                            disabled={isSendingReply}
                                        />
                                        <div className="reply-actions">
                                            <button className="reply-send-btn" onClick={handleSendReply} disabled={!replyBody || isSendingReply}>
                                                {isSendingReply ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
                                                <span>Send Reply</span>
                                            </button>
                                        </div>
                                    </div>
                            </div>
                    ) : null}
                </main>
            </div>

            {/* Compose Modal */}
            {isComposeOpen && (
                <>
                    <div className="compose-backdrop" onClick={() => setIsComposeOpen(false)} />
                    <div className="compose-overlay-premium">
                        <div className="compose-header">
                            <div className="header-text">
                                <EditIcon fontSize="small" />
                                <span>New Message</span>
                            </div>
                            <IconButton size="small" onClick={() => setIsComposeOpen(false)} style={{ color: '#fff' }}>
                                <CloseIcon />
                            </IconButton>
                        </div>

                        <div className={`compose-body-container ${showAssistant ? 'with-assistant' : ''}`}>
                            {/* Left: Message Editor */}
                            <div className="compose-editor">
                                <div className="editor-scroll-body">
                                    <div className="targeting-tabs">
                                    {(userRole === 'ADMIN' || userRole === 'FACULTY') && (
                                        <button className={`tab-btn ${targetingTab === 'global' ? 'active' : ''}`} onClick={() => setTargetingTab('global')}>Broadcast</button>
                                    )}
                                    <button className={`tab-btn ${targetingTab === 'class' ? 'active' : ''}`} onClick={() => setTargetingTab('class')}>By Class</button>
                                    <button className={`tab-btn ${targetingTab === 'individual' ? 'active' : ''}`} onClick={() => setTargetingTab('individual')}>Individuals</button>
                                </div>

                                <div className="targeting-controls">
                                    {targetingTab === 'global' && (
                                        <div className="global-options">
                                            {['ALL', 'STUDENTS', 'FACULTY', 'STAFF'].map(t => (
                                                <button
                                                    key={t}
                                                    className={`option-chip ${globalTarget === t ? 'selected' : ''}`}
                                                    onClick={() => setGlobalTarget(t)}
                                                >
                                                    {t === 'ALL' ? 'Entire School' : `All ${t}`}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {targetingTab === 'class' && (
                                        <div className="class-selector">
                                            <Select
                                                isMulti
                                                options={classOptions}
                                                placeholder="Select classes..."
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                value={selectedClasses}
                                                onChange={setSelectedClasses}
                                            />
                                        </div>
                                    )}

                                    {targetingTab === 'individual' && (
                                        <div className="individual-selector-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {/* Role filter: shown to Admin and Faculty */}
                                            {(userRole === 'ADMIN' || userRole === 'FACULTY') && (
                                                <div className="role-filter" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>Filter by Role:</span>
                                                    <div style={{ flex: 1 }}>
                                                        <Select
                                                            options={[
                                                                { value: 'STUDENT', label: 'Students' },
                                                                { value: 'FACULTY', label: 'Faculty' },
                                                                { value: 'STAFF', label: 'Staff' },
                                                                { value: 'ADMIN', label: 'Admins' },
                                                            ]}
                                                            placeholder="Filter by Role..."
                                                            className="react-select-container"
                                                            classNamePrefix="react-select"
                                                            value={{
                                                                value: individualRoleFilter,
                                                                label: individualRoleFilter === 'STUDENT' ? 'Students' : individualRoleFilter === 'FACULTY' ? 'Faculty' : individualRoleFilter === 'STAFF' ? 'Staff' : 'Admins'
                                                            }}
                                                            onChange={(opt) => {
                                                                const val = opt ? opt.value : 'STUDENT';
                                                                setIndividualRoleFilter(val);
                                                                // Clear class filter if switching away from students
                                                                if (val !== 'STUDENT') setSelectedClassForFilter(null);
                                                            }}
                                                            styles={customSelectStyles}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Class filter: shown when viewing students */}
                                            {(userRole === 'FACULTY' || individualRoleFilter === 'STUDENT') && (
                                                <div className="class-filter" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>Filter Students by Class:</span>
                                                    <div style={{ flex: 1 }}>
                                                        <Select
                                                            options={classOptions}
                                                            placeholder="Select Class..."
                                                            className="react-select-container"
                                                            classNamePrefix="react-select"
                                                            value={selectedClassForFilter}
                                                            onChange={handleClassFilterChange}
                                                            isClearable
                                                            styles={customSelectStyles}
                                                        />
                                                    </div>
                                                    {selectedClassForFilter && selectedClassForFilter.value && (
                                                        <button
                                                            type="button"
                                                            className="option-chip selected"
                                                            style={{
                                                                background: '#014486',
                                                                color: '#fff',
                                                                padding: '8px 16px',
                                                                borderRadius: '10px',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                            onClick={() => {
                                                                const studentsOfClass = userOptions.filter(u => u.type === 'STUDENT');
                                                                setSelectedIndividualUsers(prev => {
                                                                    const others = prev.filter(p => !studentsOfClass.some(s => s.value === p.value));
                                                                    return [...others, ...studentsOfClass];
                                                                });
                                                            }}
                                                        >
                                                            Select All ({userOptions.filter(u => u.type === 'STUDENT').length})
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            <Select
                                                isMulti
                                                options={(userRole === 'ADMIN' || userRole === 'FACULTY')
                                                    ? userOptions.filter(u => u.type === individualRoleFilter)
                                                    : userOptions.filter(u => u.type === 'STUDENT')
                                                }
                                                placeholder="Search individuals..."
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                value={selectedIndividualUsers}
                                                onChange={setSelectedIndividualUsers}
                                                isLoading={isLoadingStudents}
                                                styles={customSelectStyles}
                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="input-group" style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            className="premium-subject-input"
                                            placeholder="Enter subject line..."
                                            value={subject}
                                            onChange={e => setSubject(e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div style={{ width: '200px' }}>
                                        <Select
                                            options={categories.map(c => ({ value: c.name, label: c.name }))}
                                            placeholder="Category"
                                            value={selectedComposeCategory ? { value: selectedComposeCategory, label: selectedComposeCategory } : null}
                                            onChange={(opt) => setSelectedComposeCategory(opt ? opt.value : null)}
                                            isClearable
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="editor-wrapper">
                                    <textarea
                                        className="premium-textarea"
                                        placeholder="Type your message here..."
                                        value={messageBody}
                                        onChange={e => setMessageBody(e.target.value)}
                                    />
                                    {attachedFiles.length > 0 && (
                                        <div className="attachment-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', margin: '8px 0' }}>
                                            {attachedFiles.map((file) => (
                                                <div key={file.id || file.fileId} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', color: '#0f172a' }}>
                                                    <FileIcon style={{ fontSize: '16px', color: '#014486' }} />
                                                    <span style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.filename}</span>
                                                    {file.status === 'UPLOADING' ? (
                                                        <CircularProgress size={12} color="primary" />
                                                    ) : (
                                                        <CloseIcon style={{ fontSize: '14px', cursor: 'pointer', color: '#64748b' }} onClick={() => removeAttachment(file.fileId || file.id)} />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {statusMsg.text && (
                                        <div className={`editor-status ${statusMsg.type}`}>
                                            {statusMsg.type === 'success' ? <CheckCircleIcon fontSize="small" /> : <WarningIcon fontSize="small" />}
                                            {statusMsg.text}
                                        </div>
                                    )}
                                </div>
                                </div>

                                <input
                                    type="file"
                                    ref={attachmentInputRef}
                                    onChange={handleAttachmentSelect}
                                    style={{ display: 'none' }}
                                />

                                <div className="editor-footer">
                                    <div className="attachment-ui" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => attachmentInputRef.current?.click()}
                                            disabled={isUploadingAttachment}
                                            title="Attach Document (Campus File System)"
                                            style={{ color: attachedFiles.length > 0 ? '#014486' : '#64748b' }}
                                        >
                                            <AttachFileIcon fontSize="small" />
                                        </IconButton>
                                        <div className="divider" style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 4px' }} />
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowAssistant(!showAssistant)}
                                            style={{ color: showAssistant ? '#014486' : '#94a3b8', background: showAssistant ? '#f0f7ff' : 'transparent' }}
                                            title="AI Assistant"
                                        >
                                            <AIIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                    <button className="premium-send-btn" onClick={handleSend} disabled={isSending || (!messageBody && attachedFiles.length === 0) || isUploadingAttachment}>
                                        {isSending ? <CircularProgress size={18} color="inherit" /> : <><SendIcon fontSize="small" /> Send</>}
                                    </button>
                                </div>
                            </div>

                            {/* Right: AI Assistant & Templates */}
                            {showAssistant && (
                                <div className="compose-assistant">
                                    <div className="assistant-header">
                                        <AIIcon fontSize="small" color="primary" />
                                        <span>AI Draft Assistant</span>
                                    </div>
                                    <div className="assistant-scroll">
                                        <div className="section-label">AI Quick Scenarios</div>
                                        <div className="scenario-grid">
                                            {AI_TEMPLATES.map(uc => (
                                                <button
                                                    key={uc.id}
                                                    className="scenario-card"
                                                    onClick={() => {
                                                        setSubject(uc.subject || '');
                                                        setMessageBody(uc.body || '');
                                                        // Optional: trigger AI refinement immediately but hardcoded might be enough
                                                        // if (uc.intent) handleAskAI(uc.intent); 
                                                    }}
                                                >
                                                    {uc.title}
                                                </button>
                                            ))}
                                        </div>

                                        <div className="section-label" style={{ marginTop: '20px' }}>Custom AI Request</div>
                                        <textarea
                                            className="ai-prompt-area"
                                            placeholder="e.g. 'Draft a polite reminder about tomorrow's football match...'"
                                            value={aiPrompt}
                                            onChange={e => setAiPrompt(e.target.value)}
                                        />
                                        <button className="ai-draft-btn" onClick={() => handleAskAI('custom')} disabled={isGenerating}>
                                            {isGenerating ? <CircularProgress size={16} /> : 'Generate with AI'}
                                        </button>

                                        <div className="ai-tip">
                                            <CheckCircleIcon style={{ fontSize: '14px', color: '#10b981' }} />
                                            <span>AI uses targeting context for better drafts.</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ERPInbox;
